import React, { useId } from 'react';

export const EDI_STATUS_KEY = '__ediStatus';
export const EDI_STATUS_COLUMN = {key: EDI_STATUS_KEY, label: 'EDI Status', type: 'text'};
export const EDI_DOCUMENTS = ['997', '856', '810'];

// Table-only header. It never becomes part of dataset/source headers or row data.
export function withEdiStatusColumn(headers = []) {
  const result = headers.filter(h => h.key !== EDI_STATUS_KEY);
  const wms = result.findIndex(h => h.key === 'wms');
  result.splice(wms < 0 ? result.length : wms, 0, EDI_STATUS_COLUMN);
  return result;
}

function statusText(kind, stage) {
  const status = String(stage?.status || 'UNAVAILABLE').toLowerCase().replace(/_/g, ' ');
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  // Render only server-supplied milestones, never a source/shipped-date fallback.
  const date = stage?.milestone_at ? new Date(stage.milestone_at) : null;
  const time = date && !Number.isNaN(date.getTime()) ? ` (${date.toLocaleString()})` : '';
  const reason = stage?.reason ? ` — ${stage.reason.toLowerCase().replace(/_/g, ' ')}` : '';
  return `${kind}: ${label}${time}${reason}`;
}

export default function EdiStatusBadges({summary, loading = false}) {
  const tooltipId = useId();
  const unavailable = !summary || EDI_DOCUMENTS.some(kind => summary[kind]?.status === 'UNAVAILABLE' || !summary[kind]);
  const accepted = loading ? [] : EDI_DOCUMENTS.filter(kind => summary?.[kind]?.status === 'ACCEPTED');
  const prior = summary?.['856']?.priorAcceptedLoad;
  const priorLoad = typeof prior?.loadId === 'string' ? prior.loadId.trim() : '';
  const currentLoad = String(summary?.identity?.load_id || '').trim();
  const showPrior = !loading && summary?.['856']?.status === 'NOT_PRESENT' && prior?.informational === true
    && priorLoad && currentLoad && priorLoad !== currentLoad;
  const details = loading ? 'Loading EDI status.' : [
    ...EDI_DOCUMENTS.map(kind => statusText(kind, summary?.[kind])),
    ...(showPrior ? [`856 accepted under prior Load ${priorLoad}; current Routing Load is ${currentLoad}.`] : []),
  ].join('\n');
  return (
    <span className="group relative inline-flex items-center gap-1 rounded focus:outline-none focus:ring-2 focus:ring-emerald-600"
      tabIndex={0} aria-label={loading ? 'Loading EDI status' : details} aria-describedby={tooltipId}>
      {loading ? <span className="text-xs text-slate-500">Loading…</span> : <>
        {accepted.map(kind => <span key={kind} className="inline-flex rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">{kind}</span>)}
        {showPrior && <span className="inline-flex rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-900">856</span>}
        {unavailable && <span className="text-xs text-slate-500">Unavailable</span>}
        {!unavailable && !accepted.length && !showPrior && <span className="text-slate-400" aria-hidden="true">—</span>}
      </>}
      <span id={tooltipId} role="tooltip" className="pointer-events-none absolute left-0 top-full z-50 hidden w-max max-w-xs whitespace-pre-line rounded bg-slate-800 px-3 py-2 text-xs font-normal text-white shadow-lg group-hover:block group-focus:block">
        {details}
      </span>
    </span>
  );
}
